import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, BehaviorSubject, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import * as moment from 'moment';
import { LatLng } from 'leaflet';
import { environment } from 'src/environments/environment';
import { IChartValue, IMetadata, ISerie, ISeriesInfo, IWidget } from 'src/app/shared/models/seriesInfo.interfaces';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  isWindAtmosphereModeled: boolean;
  isHidrographyModeled: boolean;
  isWavesModeled: boolean;
  initialVelocityUnit: string;

  // #region Widget Data
  private windAtmosphereDataSubject = new BehaviorSubject<IWidget>({
    charts: { yAxis: [] },
    tables: [],
    lastValues: []
  });
  windAtmosphereData$: Observable<IWidget> = this.windAtmosphereDataSubject.asObservable();

  private hidrographyDataSubject = new BehaviorSubject<IWidget>({
    charts: { yAxis: [] },
    tables: [],
    lastValues: []
  });
  hidrographyData$: Observable<IWidget> = this.hidrographyDataSubject.asObservable();

  private wavesDataSubject = new BehaviorSubject<IWidget>({
    charts: { yAxis: [] },
    tables: [],
    lastValues: []
  });
  wavesData$: Observable<IWidget> = this.wavesDataSubject.asObservable();
  // #endregion

  constructor(private http: HttpClient) {}

  start() {
    this.getData();
    setInterval(() => this.getData(), 60000); 
  }

  getSiteSettings = () => this.http.get(`assets/${environment.project}/site_settings.json`).pipe(take(1));

  getChartSettings = () => this.http.get(`assets/${environment.project}/chartSettings.json`).pipe(take(1));

  getData() {
    this.http.get(`assets/${environment.project}/seriesInfo.test.json`).pipe(take(1)).subscribe((info: ISeriesInfo) => {
      this.isWindAtmosphereModeled = info.isWindAtmosphereModeled;
      this.isHidrographyModeled = info.isHydrographyModeled;
      this.isWavesModeled = info.isWavesModeled;
      this.initialVelocityUnit = info.velocityUnit;

      this.processDataForWidget(info.windAndAtmosphereWidgets, this.windAtmosphereDataSubject);
      this.processDataForWidget(info.hidrographyWidgets, this.hidrographyDataSubject);
      this.processDataForWidget(info.wavesWidgets, this.wavesDataSubject);
    });
  }

  // #region Requests
  private getMetadata(series: ISerie[]) : Observable<IMetadata[]> {
    let seriesGrouped = this.groupSeriesByServer(series);

    let requests = Object.keys(seriesGrouped).map(server => {
      let ids = seriesGrouped[server].join(',');
      let url = `${server}GetTimeSeriesById?ids=${ids}&format=json`;

      return this.http.get<any>(url, { observe: 'response'})
        .pipe(map(res => res.body.map(data => {
          return {
            id: data.Id,
            stationCode: data.Station.StationCode,
            locationName: data.Station.Location.Name,
            locationCoords: new LatLng(
              data.Station.Location.Latitude,
              data.Station.Location.Longitude
            ),
            parameterName: data.Parameter.Parameter.Name,
            unit: data.Parameter.Unit,
            sourceName: data.Source.Name,
          }}),
          catchError(() => of([]))
        ));
    });

    return forkJoin(requests).pipe(map(results => [].concat(...results)));
  }

  private getAllLastValues(series: ISerie[]) : Observable<IChartValue[]> {
    // Get all servers for the series
    let seriesGrouped = this.groupSeriesByServer(series);

    let requests = Object.keys(seriesGrouped).map(server => {
      let ids = seriesGrouped[server].join(',');
      let date = moment(new Date()).toISOString();
      let url = `${server}GetTimeSerieValueBeforeDateByTimeSerieIds/${ids}/${date}/?format=json`;

      return this.http.get<any>(url, { observe: 'response'})
        .pipe(map(res => res.body.Values.map(data => {
          return {
            date: moment(new Date(res.body.CalculationTime.match(/\d+/)[0] * 1)).toDate(),
            value: data.Value,
            id: data.TimeSerieId,
            isMeasure: series.find(function(e, i) { if (e.id == data.TimeSerieId) return e;}).isMeasure
          }}),
          catchError(() => of([]))
        ));
    });

    return forkJoin(requests).pipe(map(results => [].concat(...results)));
  }

  private getChartOrTableValues(serie: ISerie) : Observable<IChartValue[]> {
    // Get all servers for the series
    let date1 = moment().subtract(serie.visibleHindcastInHours, 'h').toDate().toISOString();
    let date2 = moment().add(5, 'd').toDate().toISOString();
    let url = `${serie.server}GetTimeSerieValuesByTimeSerieId/${serie.id}/${date1}/${date2}/?format=json`;
    
    return this.http.get<any>(url, { observe: 'response' })
      .pipe(map((res) => res.body.Values.map((data) => {
          return {
            date: moment(new Date(data.DateTime.match(/\d+/)[0] * 1)).toDate(),
            value: data.Value,
            id: res.body.Request.TimeSerieId,
          }})
        ),
        catchError(() => of([]))
      );
  }
  // #endregion

  // #region Data Process
  private processDataForWidget(widget: IWidget, subject: BehaviorSubject<IWidget>) : void {
    // Get metadata
    let allSeriesFromWidget = this.getAllSeries(widget) // Get all series from widget to get the metadata from
    let widgetMetadata = this.getMetadata(allSeriesFromWidget);

    // Get last values
    let widgetLastValues = this.getAllLastValues(widget.lastValues);

    // Get chart values
    let widgetChartValues: Observable<IChartValue[]>[] = [];
    widget.charts.yAxis.forEach(axis => {
      axis.series.forEach(serie => widgetChartValues.push(this.getChartOrTableValues(serie)))
    });

    // Get table values
    let widgetTableValues = widget.tables && widget.tables.length > 0 
      ? widget.tables.map(serie => this.getChartOrTableValues(serie)) : [];

    forkJoin([widgetMetadata, widgetLastValues, ...widgetChartValues, ...widgetTableValues]).subscribe(response => {
      // Get Metadata for processing while processing each series
      let metadata = response[0] as IMetadata[]; 

      // Process last values
      let lastValues = response[1] as IChartValue[];
      widget.lastValues.forEach(serie => {
        let lastValue = lastValues.find(lv => lv.id === serie.id);
        serie.lastValue = lastValue.value;
        serie.date = lastValue.date;
        
        Object.assign(serie, metadata.find(m => m.id === serie.id));
      });

      // Process chart & table data
      // Knowing that chartValues start right after the first two responses
      let chartDataStartIndex = 2;
      let chartDataEndIndex = chartDataStartIndex + widgetChartValues.length;

      this.processChartOrTableData(
        widget.charts.yAxis.map(axis => axis.series),
        response.slice(chartDataStartIndex, chartDataEndIndex) as IChartValue[][],
        metadata,
        v => ({
          id: v.id,
          value: v.value,
          date: v.date
        })
      );

      if (widget.tables && widget.tables.length > 0) {
        this.processChartOrTableData(
          [widget.tables],
          response.slice(chartDataEndIndex) as IChartValue[][],
          metadata,
          v => ({
            id: v.id,
            value: v.value,
            date: v.date
          })
        )
      }
      subject.next(widget);
    });
  }

  private getAllSeries(widget: IWidget) : ISerie[] {
    let chartSeries: ISerie[] = [];
    widget.charts.yAxis.forEach(axis => chartSeries.push(...axis.series));

    return !widget.tables || widget.tables.length === 0
      ? [...chartSeries, ...widget.lastValues]
      : [...chartSeries, ...widget.lastValues, ...widget.tables];
  }

  private groupSeriesByServer(series: ISerie[]) : Record<string, number[]> {
    return series.reduce((acc, series) => {
      if (acc[series.server]){
        acc[series.server].push(series.id)
      } else {
        acc[series.server] = [series.id];
      }

      return acc;
    }, {} as Record<string, number[]>);
  }

  private processChartOrTableData(
    targetContainer: ISerie[][],
    data: IChartValue[][],
    metadata: IMetadata[],
    mapFunction: (value: IChartValue) => IChartValue
  ): void {
    let _data = new Map(data.filter(d => d.length > 0).map(d => [d[0].id, d]))

    targetContainer.forEach(container => {
      container.forEach(series => {
        if (_data.get(series.id) !== undefined) {
          series.chartValues = _data.get(series.id).map(mapFunction);
          let serieMetadata = metadata.find(m => m.id === series.id);
          if (serieMetadata) {
            Object.assign(series, serieMetadata);
          }
        }
      });
    });
  }
  // #endregion
}
